import { useQuery } from '@tanstack/react-query'
import { useEffect, useRef, useState } from 'react'
import { Link, Navigate, useSearchParams } from 'react-router-dom'

import { api, credentialRefused } from '../api/client'
import { t } from '../i18n'
import { Notice, Pending } from '../ui'
import { useSession } from './session'

/**
 * The way in, and the instance says which one it is running.
 *
 * `/healthz` answers without an identity on every node — which is what made it
 * this screen's first call long before it named the mode — and its `sign_in`
 * field says which of three doors this box has:
 *
 * `password` — the instance is its own authority. It shows this form, takes an
 * email address and a password, and issues a session. That is what a node with
 * a public address and no directory in front of it runs, and it is what
 * replaced the browser's own authentication dialogue on the alpha: a front door
 * the browser draws cannot be branded, cannot say what the site is, cannot
 * offer a password reset, and is the same credential for everybody who gets in.
 *
 * `jwt` — a directory issues a short-lived EdDSA token and every box verifies
 * it locally against a cached JWKS, which is what lets sign-in survive an
 * uplink outage. That flow belongs with the directory build and is stubbed.
 *
 * `trusted-header` — a laptop running the stack locally. The person comes from
 * a request header and nothing is verified, so the form asks for an identifier
 * and says as much.
 *
 * **The provider's button is not a fourth mode**, which is why it is drawn from
 * `federated_sign_in` and not from `sign_in`. An instance configured with an
 * identity provider offers that door *as well as* the one its mode names, so a
 * provider misconfigured on a Friday afternoon cannot shut a site out of its
 * own software. It is a plain form rather than a button that navigates: the
 * whole point of this door is that the browser leaves for the provider and
 * comes back, and a browser leaves a page perfectly well without any script.
 *
 * **What comes back in the address is a code, not a session.** The callback is
 * a redirect, so anything it returned would be written into history, into the
 * referrer of the next request and into every proxy log on the way; the code is
 * single-use, lives about two minutes, and is spent here in a request of its
 * own and taken out of the address before anything else can read it.
 *
 * **"Cannot sign in?" now sends something.** The instance mails a link that
 * opens `/signin/reset`, where a second factor is asked for as well; somebody
 * whose address is wrong or whose phone is gone still asks whoever administers
 * the directory for a one-time code and types it on the same screen. Both
 * doors are offered here, because the person who needs the second one is the
 * person the first one failed.
 *
 * **The second factor appears after a password is accepted and never before.**
 * There is no way for this screen to know whether an address has one — asking
 * would be a way of finding out who has what — so the field is rendered when
 * the instance refuses with `second_factor_required`, which is a refusal only
 * somebody who typed the right password can earn. The password stays in its
 * field for that second submit; the browser already has it.
 */
export function SignIn() {
  const { credentials, signIn, me, refused } = useSession()
  const [params, setParams] = useSearchParams()
  // The welcome page hands the address over in `?email=` so it is not typed
  // twice. It fills the field and nothing else; nothing is sent until Sign in.
  const [email, setEmail] = useState(() => params.get('email') ?? '')
  const [password, setPassword] = useState('')
  const [second, setSecond] = useState('')
  const [personId, setPersonId] = useState('')
  const [refusal, setRefusal] = useState<Refusal>(null)
  const [sending, setSending] = useState(false)
  // A code is spent once. React runs an effect twice in development on
  // purpose, and a second exchange of a single-use code is a refusal — so the
  // guard is a ref rather than state, because state would be read from the
  // render that scheduled the effect and both runs would see it unset.
  const spent = useRef(false)

  const health = useQuery({
    queryKey: ['healthz'],
    retry: false,
    queryFn: async () => (await api.GET('/healthz', {})).data,
  })

  // Coming back from the provider, either way it went.
  useEffect(() => {
    if (params.get('signin_failed') !== null) {
      setRefusal({ kind: 'federated_failed' })
      setParams(without(params, 'signin_failed'), { replace: true })
      return
    }
    const code = params.get('signin')
    if (code === null || spent.current) return
    spent.current = true
    // Out of the address first and awaited second: the exchange takes a moment
    // and the address is readable for every one of them.
    setParams(without(params, 'signin'), { replace: true })
    setSending(true)
    void (async () => {
      try {
        const result = await api.POST('/signin/federated/session', { body: { code } })
        if (result.data !== undefined) {
          signIn({ mode: 'federated', token: result.data.token })
          return
        }
        setRefusal({ kind: 'federated_failed' })
      } catch {
        setRefusal({ kind: 'unreachable' })
      } finally {
        setSending(false)
      }
    })()
  }, [params, setParams, signIn])

  // The wait counts itself down, for the reason the keypad's does: a number
  // that sits still reads as a page that has frozen, and somebody who cannot
  // see it shrinking goes looking for an administrator they do not need.
  useEffect(() => {
    if (refusal?.kind !== 'waiting') return
    if (refusal.seconds <= 0) {
      setRefusal(null)
      return
    }
    const timer = setTimeout(
      () => setRefusal({ kind: 'waiting', seconds: refusal.seconds - 1 }),
      1000,
    )
    return () => clearTimeout(timer)
  }, [refusal])

  // Not while the instance is refusing this credential. `me.data` is the last
  // answer that worked, and React Query keeps it beside a failed refetch, so
  // without this clause a browser holding a stale success and a 401 bounced
  // between here and the gate for ever and the sentence below was never
  // reached. The provider drops a refused credential, which closes it at the
  // other end too; both are here because either one alone leaves a render in
  // which they disagree.
  if (credentials && me.data && !credentialRefused(me.error) && !refused) {
    return (
      <Navigate to={landingFor(me.data.person.platform_role, params.get('return_to'))} replace />
    )
  }

  const mode = health.data?.sign_in
  const federated = health.data?.federated_sign_in === true
  const waiting = refusal?.kind === 'waiting' ? refusal.seconds : 0

  async function submitPassword(): Promise<void> {
    setSending(true)
    try {
      const result = await api.POST('/signin/password', {
        body: { email, password, second_factor_code: second },
      })
      if (result.data !== undefined) {
        signIn({ mode: 'password', token: result.data.token })
        return
      }
      if (result.response.status === 429) {
        setPassword('')
        setSecond('')
        setRefusal({ kind: 'waiting', seconds: retryAfter(result.response) })
        return
      }
      const key =
        (result.error as { detail?: { message_key?: string } } | undefined)?.detail
          ?.message_key ?? ''
      if (key === 'signin.error.second_factor_required') {
        // Nothing was wrong with what was sent. The password is deliberately
        // left in its field: clearing it would make somebody type it again to
        // add six digits they were only just asked for.
        setSecond('')
        setRefusal({ kind: 'second_factor' })
        return
      }
      setPassword('')
      setSecond('')
      if (key === 'signin.error.second_factor_unavailable') {
        setRefusal({ kind: 'second_factor_unavailable' })
        return
      }
      setRefusal({ kind: 'refused' })
    } catch {
      setRefusal({ kind: 'unreachable' })
    } finally {
      setSending(false)
    }
  }

  return (
    <div className="signin">
      <h2 className="page">{t('app.name')}</h2>
      <p className="page-sub">{t('signin.title')}</p>

      {health.isPending ? <Pending /> : null}
      {health.error ? <Notice tone="bad">{t('signin.unreachable')}</Notice> : null}
      {/* A credential the browser is holding that the instance will not
          answer to. In `password` mode that is a session that has run out or
          been signed out elsewhere, and it gets its own sentence, because
          "wrong password" is not what happened and sends somebody to reset one
          they still know. In `trusted-header` mode it is the identifier just
          typed naming nobody, which is a refusal of that form.

          A refusal and nothing else. This used to read `me.error`, so a 500 or
          a proxy answering while the container came back put somebody here and
          told them their session had expired, and the mail link below then
          offered to reset a password that was never wrong. */}
      {refused || (credentialRefused(me.error) && credentials) ? (
        <Notice tone="bad">
          {t(mode === 'trusted-header' ? 'signin.failed' : 'signin.expired')}
        </Notice>
      ) : null}
      {refusal?.kind === 'refused' ? (
        <Notice tone="bad">
          {t(mode === 'password' ? 'signin.password.failed' : 'signin.failed')}
        </Notice>
      ) : null}
      {refusal?.kind === 'federated_failed' ? (
        <Notice tone="bad">{t('signin.federated.failed')}</Notice>
      ) : null}
      {refusal?.kind === 'second_factor' ? (
        <Notice tone="warn">{t('signin.second_factor.asked')}</Notice>
      ) : null}
      {refusal?.kind === 'second_factor_unavailable' ? (
        <Notice tone="bad">{t('reset.second_unavailable')}</Notice>
      ) : null}
      {refusal?.kind === 'unreachable' ? (
        <Notice tone="bad">{t('signin.unreachable')}</Notice>
      ) : null}
      {waiting > 0 ? (
        <Notice tone="warn">{t('signin.waiting').replace('{seconds}', String(waiting))}</Notice>
      ) : null}

      {/* Above the password form on purpose: on an instance that has both, the
          provider is the door almost everybody uses, and the password form
          below it is the one that still works when the provider is down or
          somebody has no account there. */}
      {federated ? (
        <form className="card" method="get" action="/signin/federated/start">
          <h3>{t('signin.federated.title')}</h3>
          <p className="muted">{t('signin.federated.explain')}</p>
          {/* Where to land afterwards. It is sent rather than assumed because
              the instance checks it against a list it holds before sending
              anybody anywhere — and this page is the honest answer: it spends
              the code and then goes wherever this person's first project is. */}
          <input type="hidden" name="return_to" value="/signin" />
          <button type="submit" className="primary" disabled={sending}>
            {t('signin.federated.submit')}
          </button>
        </form>
      ) : null}

      {mode === 'password' ? (
        <form
          className="card"
          onSubmit={(event) => {
            event.preventDefault()
            if (sending || waiting > 0) return
            if (email.trim().length === 0 || password.length === 0) {
              setRefusal({ kind: 'refused' })
              return
            }
            setRefusal(null)
            void submitPassword()
          }}
        >
          <h3>{t('signin.password.title')}</h3>
          <div className="field">
            <label htmlFor="email">{t('signin.password.email')}</label>
            <input
              id="email"
              name="email"
              type="email"
              autoComplete="username"
              spellCheck={false}
              value={email}
              onChange={(event) => setEmail(event.target.value)}
            />
          </div>
          <div className="field">
            <label htmlFor="password">{t('signin.password.password')}</label>
            <input
              id="password"
              name="password"
              type="password"
              autoComplete="current-password"
              value={password}
              onChange={(event) => setPassword(event.target.value)}
            />
          </div>
          {refusal?.kind === 'second_factor' ? (
            <div className="field">
              <label htmlFor="second-factor">{t('signin.second_factor.label')}</label>
              <input
                id="second-factor"
                name="second-factor"
                className="mono"
                inputMode="numeric"
                autoComplete="one-time-code"
                spellCheck={false}
                autoFocus
                value={second}
                onChange={(event) => setSecond(event.target.value)}
              />
            </div>
          ) : null}
          <button type="submit" className="primary" disabled={sending || waiting > 0}>
            {t('signin.password.submit')}
          </button>
          <p className="muted">
            {t('signin.password.forgot')}{' '}
            <Link to="/signin/forgot">{t('signin.password.mail_link')}</Link>
          </p>
          <p className="muted">
            <Link to="/signin/reset">{t('signin.password.reset_link')}</Link>
          </p>
        </form>
      ) : null}

      {mode === 'trusted-header' ? (
        <form
          className="card"
          onSubmit={(event) => {
            event.preventDefault()
            const trimmed = personId.trim()
            if (trimmed.length === 0) {
              setRefusal({ kind: 'refused' })
              return
            }
            setRefusal(null)
            signIn({ mode: 'trusted-header', personId: trimmed })
          }}
        >
          <h3>{t('signin.trusted_header.title')}</h3>
          <p className="muted">{t('signin.trusted_header.explain')}</p>
          <div className="field">
            <label htmlFor="person">{t('signin.trusted_header.label')}</label>
            <input
              id="person"
              name="person"
              autoComplete="off"
              spellCheck={false}
              value={personId}
              onChange={(event) => setPersonId(event.target.value)}
            />
          </div>
          <button type="submit" className="primary">
            {t('signin.trusted_header.submit')}
          </button>
        </form>
      ) : null}

      {mode === 'jwt' ? (
        <div className="card">
          <h3>{t('signin.directory.title')}</h3>
          <p className="muted">{t('signin.directory.explain')}</p>
          <button type="button" disabled>
            {t('signin.directory.submit')}
          </button>
        </div>
      ) : null}
    </div>
  )
}

type Refusal =
  /** The password was looked at and was wrong, or the address is nobody's. One answer. */
  | { kind: 'refused' }
  /** The password was right and this person is enrolled. Nothing is wrong yet. */
  | { kind: 'second_factor' }
  /** This instance cannot read the second factor it holds, which is not theirs to fix. */
  | { kind: 'second_factor_unavailable' }
  /** A trip out to the provider that did not end in a session. One answer for
   * every way it can fail, decided on the instance: a person who is not on this
   * site and somebody probing the callback see the same sentence. */
  | { kind: 'federated_failed' }
  /** The door has not looked at it yet: it is still waiting. */
  | { kind: 'waiting'; seconds: number }
  /** Nothing answered at all, which is a different thing to say. */
  | { kind: 'unreachable' }
  | null

/**
 * Where a sign-in lands.
 *
 * An explicit `return_to` on the sign-in address wins, when it is a path on
 * this site — never another origin, and never the sign-in page itself. After
 * that, somebody who runs the site (a Global Admin or a Super Admin)
 * lands on Administration, which is the page they signed in to use; everybody
 * else lands where they always have, on their projects.
 */
export function landingFor(role: string | null | undefined, returnTo: string | null): string {
  if (
    returnTo &&
    returnTo.startsWith('/') &&
    !returnTo.startsWith('//') &&
    !returnTo.startsWith('/\\') &&
    !returnTo.startsWith('/signin')
  ) {
    return returnTo
  }
  return role ? '/admin' : '/'
}

/** The same address without one parameter, for taking a spent code out of it. */
function without(params: URLSearchParams, name: string): URLSearchParams {
  const next = new URLSearchParams(params)
  next.delete(name)
  return next
}

/** The seconds left on a wait, from the header the refusal carries. */
export function retryAfter(response: Response): number {
  const header = Number(response.headers.get('Retry-After'))
  return Number.isFinite(header) && header > 0 ? Math.ceil(header) : 1
}
